import { Box, Grid, Tooltip } from '@mui/material';
import KeyValueComponent, { DataJsonType } from './KeyValueComponent';
import React, { FC, useEffect, useMemo, useState, useContext } from 'react';
import MediumTypography from '../formlib/MediumTypography';
import ThomProgram from '../../assets/images/thomchildlogo.svg';
import { Client, Parent, Program } from '../../pages/eligibility/types';
import { convertGenderToFrontend } from '../../utils/genderUtil';
import { removeDoubleCommas } from '../../utils/nameUtils';
import { LookUpContext } from '../../context/LookUpContextProvider';
import {
  DownloadClientFilesResponse,
  getClientFilesUrl,
} from '../../services/configApi/clientFiles/clientFilesProvider';
import { ReactComponent as DPHLogo } from '../../assets/images/dphLogo.svg';

export interface ProgramPanelPropType {
  program?: Program;
  client?: Client;
  parents?: Parent[];
  showIntakeFacilitator?: boolean;
  showAdditionalDemographics?: boolean;
  showDHPLogo?: boolean;
  isProgramDirectorRequired?: boolean;
}
export interface ProgramProps {
  labels: DataJsonType[];
  titleLabelId: string;
  titleDefaultLabel: string;
  titleValue: string;
  imageUrl: string;
}
interface ProgramParent {
  data: DataJsonType[];
}
const ProgramPanel: FC<ProgramPanelPropType> = ({
  program,
  client,
  parents,
  showIntakeFacilitator,
  showAdditionalDemographics,
  showDHPLogo = false,
  isProgramDirectorRequired = false,
}) => {
  const [clientDetails, setClientDetails] = useState<DataJsonType[]>([]);
  const [clientAdditionalDetails, setClientAdditionalDetails] = useState<
    DataJsonType[]
  >([]);
  const [programDetails, setProgramDetails] = useState<ProgramProps | null>(
    null,
  );
  const [programAddressDetails, setProgramAddressDetails] = useState<
    DataJsonType[]
  >([]);
  const [parentDetails, setParentDetails] = useState<ProgramParent[]>([]);
  const [privateImageUrl, setPrivateImageUrl] = useState('');

  const { lookups } = useContext(LookUpContext);

  const constructClientDetails = () => {
    const clientKeys: DataJsonType[] = [];
    if (client) {
      clientKeys.push({
        id: 'clientName',
        labelid: 'ProgressNote.ClientName',
        defaultLabel: 'ClientName:',
        value: client.name ?? '-',
      });
      clientKeys.push({
        id: 'sex',
        labelid: 'ProgressNote.Sex',
        defaultLabel: 'Sex:',
        value: `${
          client.sex !== null ? convertGenderToFrontend(client.sex) : '-'
        }`,
      });
      clientKeys.push({
        id: 'dphId',
        labelid: 'ProgressNote.DPHID',
        defaultLabel: 'Enrollment ID:',
        value: client.dphId ?? '-',
      });
      clientKeys.push({
        id: 'dob',
        labelid: 'ProgressNote.DateofBirth',
        defaultLabel: 'Date of Birth:',
        value: client.dob ?? '-',
      });
      clientKeys.push({
        id: 'rcd',
        labelid: 'sspReferral.referralDate',
        defaultLabel: 'Referral Begin Date:',
        value: client.referralBeginDate ?? '-',
      });
      clientKeys.push({
        id: 'serviceCoordinator',
        labelid: 'sspReferral.serviceCoordinator',
        defaultLabel: 'Service Cooridinator:',
        value: `${
          client.serviceCoordinatorName ? client.serviceCoordinatorName : ''
        }${
          client.serviceCoordinatorPhone
            ? ', ' + client.serviceCoordinatorPhone
            : ''
        }${
          client.serviceCoordinatorPhoneExtension
            ? ',  Ext: ' + client.serviceCoordinatorPhoneExtension
            : ''
        }`,
      });
      if (showIntakeFacilitator) {
        clientKeys.push({
          id: 'intakeFacilitator',
          labelid: 'ProgramPanel.intakeFacilitator',
          defaultLabel: 'Intake Facilitator:',
          value: client.intakeFacilitator ?? '-',
        });
      }
    }
    setClientDetails(clientKeys);
  };

  const getPediatricianName = useMemo(
    () =>
      (clientForPed: Client): string => {
        let pediatricianName = '';
        if (clientForPed.pediatricianName !== null) {
          pediatricianName = clientForPed.pediatricianName;
        }
        if (clientForPed.pediatricianPhoneNumber !== null) {
          pediatricianName =
            pediatricianName + ', ' + clientForPed.pediatricianPhoneNumber;
        }
        return pediatricianName;
      },
    [],
  );

  const constructAdditionalClientDetails = () => {
    if (showAdditionalDemographics && client) {
      const clientKeys: DataJsonType[] = [];
      const demographicsDetails = getAdditionalDemographicData(client);
      clientKeys.push(...demographicsDetails, {
        id: 'pediatrician',
        labelid: 'pediatricianText',
        defaultLabel: 'Pediatrician : ',
        value: getPediatricianName(client),
      });
      setClientAdditionalDetails(clientKeys);
    }
  };

  const getLanguageDescription = (code: string) => {
    if (lookups?.language) {
      const language = lookups.language.find((lang) => lang.code === code);
      return language ? language.description : '-';
    }
    return '-';
  };

  const getRaceDescription = (code: string) => {
    if (lookups?.race) {
      const race = lookups.race.find((r) => r.code === code);
      return race ? race.description : '-';
    }
    return '-';
  };

  const getEthnicityDescription = (code: string) => {
    if (lookups?.ethnicity) {
      const ethnicity = lookups.ethnicity.find((eth) => eth.code === code);
      return ethnicity ? ethnicity.description : '-';
    }
    return '-';
  };

  const getAdditionalDemographicData = (clientData: Client): DataJsonType[] => {
    const data: DataJsonType[] = [];

    data.push(
      {
        id: 'spokenLanguage',
        labelid: 'primarySpokenLanguage',
        defaultLabel: 'Primary Spoken Language : ',
        value:
          clientData.primarySpokenLanguage === null
            ? '-'
            : getLanguageDescription(clientData.primarySpokenLanguage),
      },
      {
        id: 'writtenLanguage',
        labelid: 'primaryWrittenLanguage',
        defaultLabel: 'Primary Written Language : ',
        value:
          clientData.primaryWrittenLanguage === null
            ? '-'
            : getLanguageDescription(clientData.primaryWrittenLanguage),
      },
      {
        id: 'race',
        labelid: 'raceText',
        defaultLabel: 'Race : ',
        value:
          clientData.race === null
            ? '-'
            : clientData.race.split(',').map(getRaceDescription).toString(),
      },
      {
        id: 'ethnicity',
        labelid: 'ethnicity',
        defaultLabel: 'Ethnicity : ',
        value:
          clientData.ethnicity === null
            ? '-'
            : getEthnicityDescription(clientData.ethnicity),
      },
    );
    return data;
  };

  const getStateAcronym = (state: string | undefined | null) => {
    if (!state) return '';
    const trimmed = state.trimEnd(); // text inside last ()
    const closeIdx = trimmed.lastIndexOf(')');
    const openIdx = closeIdx > 0 ? trimmed.lastIndexOf('(', closeIdx) : -1;
    if (openIdx >= 0 && closeIdx === trimmed.length - 1) {
      return trimmed.substring(openIdx + 1, closeIdx); // use acronym if found
    }
    return state; // fallback to full state
  };

  const constructProgramDetails = () => {
    const topLabels: DataJsonType[] = [];
    const addressLabels: DataJsonType[] = [];

    if (program) {
      // Line 1 → Phone, Fax, Website
      topLabels.push(
        {
          id: 'phone',
          labelid: 'ConsentForms.contactPhone',
          defaultLabel: 'Phone:',
          value: program.phoneNumber ?? '-',
        },
        {
          id: 'fax',
          labelid: 'ProgressNote.Fax',
          defaultLabel: 'Fax:',
          value: program.faxNumber ?? '-',
        },
        {
          id: 'website',
          labelid: 'ConsentForms.programWebsite',
          defaultLabel: 'Website:',
          value: program.website ?? '-',
        },
      );

      // Line 2 → Address (always separate row)
      addressLabels.push({
        id: 'address',
        labelid: 'addressTextEics',
        defaultLabel: 'Address:',
        value:
          [
            program.addressLine1,
            program.addressLine2,
            program.city,
            getStateAcronym(program.state),
            program.zipCode,
          ]
            .filter(Boolean)
            .join(', ') || '-',
      });

      setProgramDetails({
        labels: topLabels, // only line 1 labels
        titleLabelId: 'ConsentForms.programName',
        titleDefaultLabel: 'Program:',
        titleValue: program.name ?? '-',
        imageUrl: program.imageUrl ?? '',
      });

      setProgramAddressDetails(addressLabels); // 👈 store separately
    }
  };

  const constructParentInfo = () => {
    const parentsValue: ProgramParent[] = [];
    parents?.forEach((parent) => {
      const p: ProgramParent = {
        data: [],
      };
      const parentInfo: DataJsonType[] = [];
      parentInfo.push({
        id: 'parentGuardianName',
        labelid: 'sspReferral.parentName',
        defaultLabel: 'Guardian Name:',
        value: parent.name ?? '-',
      });
      parentInfo.push({
        id: 'contactRelationship',
        labelid: 'Facesheet.relationship',
        defaultLabel: 'Guardian Type:',
        value: parent.type
          ? parent.type.charAt(0).toUpperCase() +
            parent.type.slice(1).toLowerCase()
          : '-',
      });
      parentInfo.push({
        id: 'contactEmail',
        labelid: 'ConsentEmail',
        defaultLabel: 'Email:',
        value: parent.email ?? '-',
      });
      parentInfo.push({
        id: 'contactPhone',
        labelid: 'ConsentForms.contactPhone',
        defaultLabel: 'Phone:',
        value: parent.homePhoneNumber ?? '-',
      });
      parentInfo.push({
        id: 'contactAddress',
        labelid: 'ConsentForms.clientAddress',
        defaultLabel: 'Address:',
        value: `${
          parent.address !== null ? removeDoubleCommas(parent.address) : '-'
        }`,
      });
      p.data = parentInfo;
      parentsValue.push(p);
    });
    setParentDetails(parentsValue);
  };

  useEffect(() => {
    constructClientDetails();
    constructAdditionalClientDetails();
    constructProgramDetails();
    constructParentInfo();
    if (program?.imageUrl) downloadFunction(program?.imageUrl ?? '');
  }, [program, client, parent]);

  const downloadFunction = (url: string) => {
    const filteredName = url.split('client-repo/')[1];
    getClientFilesUrl(filteredName)
      .then((response: DownloadClientFilesResponse) => {
        setPrivateImageUrl(response.url);
      })
      .catch(() => {});
  };
  return (
    <Grid>
      <Grid
        container
        direction={'row'}
        className="justifyContentSpaceBtw"
        alignItems={'center'}
      >
        <Grid item xs={8} className="flex__">
          {showDHPLogo && (
            <Box className="pr-md">
              <DPHLogo style={{ height: '110px', width: '110px' }} />
            </Box>
          )}
          <Box>
            <Box className="flex__">
              <MediumTypography
                labelid={
                  programDetails === null ? '' : programDetails.titleLabelId
                }
                defaultlabel={
                  programDetails === null
                    ? ''
                    : programDetails.titleDefaultLabel
                }
                sxProps={{
                  fontFamily: 'Lato-Bold',
                  fontSize: '20px',
                  color: '#97A6A5',
                }}
              />
              {programDetails && (
                <MediumTypography
                  className="pl-sm"
                  sxProps={{
                    color: '#2A4241',
                    fontFamily: 'Lato-Bold',
                    fontSize: '20px',
                  }}
                  label={
                    programDetails.titleValue === null ||
                    programDetails.titleValue === ''
                      ? '-'
                      : programDetails.titleValue
                  }
                />
              )}
            </Box>
            <MediumTypography
              labelid={'Program.programCertified'}
              defaultlabel={
                'A program certified by the Massachusetts Department of Public Health'
              }
              sxProps={{
                fontStyle: 'italic',
              }}
              className="pb-sm"
            />
            {isProgramDirectorRequired && program && (
              <Box className="flex__ mb-sm">
                <MediumTypography
                  labelid={'sspReferral.programDirector'}
                  defaultlabel={'Program Director:'}
                  textColor="#97A6A5"
                  fontSize="16px"
                />

                <Tooltip
                  title={
                    program.programDirector &&
                    program.programDirector.length > 10
                      ? `${program.programDirector}`
                      : ''
                  }
                  followCursor
                  arrow={true}
                >
                  <Box className="width150px ">
                    <MediumTypography
                      fontSize="16px"
                      textColor="#2A4241"
                      sxProps={{
                        paddingLeft:
                          program.programDirector === '' ||
                          program.programDirector === null ||
                          program.programDirector === undefined
                            ? '24px'
                            : '6px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                      label={
                        program.programDirector === '' ||
                        program.programDirector === null ||
                        program.programDirector === undefined
                          ? '-'
                          : `${program.programDirector}`
                      }
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}
            <KeyValueComponent
              data={programDetails ? programDetails.labels : []}
            />
            {/* Line 2 → Address */}
            {programAddressDetails.length > 0 && (
              <Box className="mt-xs">
                <KeyValueComponent
                  data={programAddressDetails}
                  isTooltipRequired={false}
                />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item className="justifyContent-End">
          <Box>
            {programDetails !== null && (
              <img
                style={{
                  width: '250px',
                  height: '63px',
                }}
                src={
                  privateImageUrl === '' || privateImageUrl === null
                    ? ThomProgram
                    : privateImageUrl
                }
                alt="program-logo"
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {clientDetails.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <Box className="defaultHorizontalLine" />
          </Grid>
          <Grid direction={'row'} item>
            <KeyValueComponent data={clientDetails} />
          </Grid>
        </Grid>
      )}

      {clientAdditionalDetails.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <Box className="defaultHorizontalLine" />
          </Grid>
          <Grid direction={'row'} item>
            <KeyValueComponent data={clientAdditionalDetails} />
          </Grid>
        </Grid>
      )}

      {parentDetails.map((parent, index) => (
        <Grid container key={'parent' + index}>
          <Grid item xs={12}>
            <Box className="defaultHorizontalLine" />
          </Grid>
          <Grid direction={'row'} item>
            <KeyValueComponent data={parent.data} />
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};
export default ProgramPanel;
